import { Grid, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'src/common/components/base-button';
import { IKeypadModal, KeypadModal } from 'src/common/components/keypad-modal/keypad-modal';
import {
  popEmeraldModal,
  pushEmeraldModal,
} from 'src/common/emerald-modal-manager/emerald-modal-manager';
import CashierPreferencesHelper from 'src/common/helpers/cashier-preferences-helper';
import { ITransactionLookupData } from 'src/common/interfaces/dtos/ITransactionLookupData';
import { IButton } from 'src/common/interfaces/IButton';
import { ReactComponent as Id } from 'src/common/media/barcode.svg';
import { ScannedDataHandler } from 'src/devices/scanner-operations';
import Keypad from 'src/keypad/components/keypad';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import { styles } from '../../search-transactions-styles';
import TransactionList from '../transaction-list/transaction-list';

const RECALL_TRANSCATION_DIALOG = 'RECALL_TRANSCATION_DIALOG';

type Props = IIRecallTransactionActionsProps;

export const showRecallScanAndGoDialog = (props: Props, onScan: ScannedDataHandler) => {
  pushEmeraldModal(<RecallTransactionModal {...props} />, onScan, RECALL_TRANSCATION_DIALOG);
};

export const closeRecallScanAndGoDialog = () => popEmeraldModal(RECALL_TRANSCATION_DIALOG);

const RecallTransactionModal = ({
  onClose,
  onRecall,
  onSearch,
  transactionsList,
}: Props): JSX.Element => {
  const [transactionListings, setTransactionListings] = useState<ITransactionLookupData[]>(
    transactionsList,
  );
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | undefined>('');

  const classes = styles();
  const { t } = useTranslation();
  const isLeftHanded = CashierPreferencesHelper.isLeftHanded();

  const handleKeyPadSubmit = (barcode: string) => {
    setSelectedTransactionId('');
    onSearch(barcode)
      .then(setTransactionListings)
      .catch(popupMessage);
  };

  const handleRecall = () => {
    onRecall(selectedTransactionId);
  };

  const onTransactionSelected = (transaction: ITransactionLookupData) => {
    if (!selectedTransactionId) {
      setSelectedTransactionId(transaction.transactionId);
    } else {
      if (selectedTransactionId === transaction.transactionId) {
        setSelectedTransactionId('');
      } else {
        setSelectedTransactionId(transaction.transactionId);
      }
    }
  };

  const keypadSubmitButton: IButton = {
    label: t('Ok'),
    onClick: handleKeyPadSubmit,
  };
  const keypadModalProps: IKeypadModal = {
    onClose: onClose,
    title: 'HeaderControl_RecallScanAndGoTransactions',
    buttonComponent: (
      <>
        <Grid item>
          <BaseButton
            buttonType='white'
            className={classes.cancelButton}
            data-testid='cancel-button-recall-transaction'
            onClick={onClose}
          >
            <span className='label'>{t('Cancel_Button')}</span>
          </BaseButton>
        </Grid>
        <Grid item className={isLeftHanded ? classes.leftHandButton : ''}>
          <BaseButton
            className={classes.listButton}
            data-testid='list-button-recall-transaction'
            onClick={handleRecall}
            disabled={selectedTransactionId ? false : true}
          >
            <span className='label'>{t('Recall')}</span>
          </BaseButton>
        </Grid>
      </>
    ),
    contentArea: (
      <Grid>
        <Grid
          data-testid='check-id-container'
          container
          item
          className={classes.iconLabelContainer}
        >
          <Id className={classes.iconLabel} />
          <Typography data-testid='scan-barcode-title-text' className={classes.scanBarcodeText}>
            {t('ResumeSelfScanTransactionView_FindTransaction')}
          </Typography>
        </Grid>
        <Grid item container className={`flexFull fullWidth ${classes.transactions}`}>
          <TransactionList
            transactions={transactionListings}
            selectedTransactionID={selectedTransactionId}
            onTransactionClick={onTransactionSelected}
          />
        </Grid>
      </Grid>
    ),
    keypad: (
      <Keypad
        visible={true}
        topLabelText={t('ResumeTransactionViewPOSNumber')}
        disableManualWeight={true}
        disableQuantity={true}
        disableDecimalInput={true}
        keypadTestIdPrefix={'input-transaction-barcode-'}
        submitButton={keypadSubmitButton}
      />
    ),
  };

  return <KeypadModal {...keypadModalProps} />;
};

interface IIRecallTransactionActionsProps {
  onClose: () => void;
  onRecall: (transactionId?: string) => void;
  onSearch: (tillId?: string) => Promise<ITransactionLookupData[]>;
  transactionsList: ITransactionLookupData[];
}

export default RecallTransactionModal;
